import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, colones, ESTADO_PEDIDO_LABEL, fmtFechaCorta } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/Panel";
import { Paginacion } from "@/components/Paginacion";
import { Switch } from "@/components/ui/switch";
import { Search, Plus } from "lucide-react";

export default function PedidosEspeciales() {
  const navigate = useNavigate();
  const { tienePermiso } = useAuth();
  const [params] = useSearchParams();
  const [pedidos, setPedidos] = useState([]);
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [soloAtrasados, setSoloAtrasados] = useState(params.get("atrasados") === "1");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get("/pedidos-especiales", {
        params: { q: q.trim() || undefined, estado: estado || undefined,
          atrasados: soloAtrasados || undefined, page, page_size: pageSize },
      });
      setPedidos(r.data.items);
      setMeta({ total: r.data.total, total_pages: r.data.total_pages });
    } finally { setCargando(false); }
  }, [q, estado, soloAtrasados, page, pageSize]);
  useEffect(() => { setPage(1); }, [q, estado, soloAtrasados, pageSize]);
  useEffect(() => { const t = setTimeout(cargar, 300); return () => clearTimeout(t); }, [cargar]);

  const diasLabel = (p) => {
    if (p.dias_para_arribo == null) return "—";
    const abs = Math.abs(p.dias_para_arribo);
    if (p.atrasado) return `ATRASADO ${abs} día${abs !== 1 ? "s" : ""}`;
    if (p.dias_para_arribo === 0) return "Llega hoy";
    return `Faltan ${p.dias_para_arribo} día${p.dias_para_arribo !== 1 ? "s" : ""}`;
  };

  return (
    <div data-testid="pedidos-especiales-page" className="space-y-4">
      <Panel titulo="Pedidos especiales" testId="pedidos-especiales-panel">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="pedidos-buscar-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por número, cliente o placa..." className="pl-9" />
            </div>
            <select data-testid="pedidos-estado-filtro" value={estado} onChange={(e) => setEstado(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white">
              <option value="">Todos los estados</option>
              {Object.entries(ESTADO_PEDIDO_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-500">
              <Switch data-testid="pedidos-solo-atrasados-switch" checked={soloAtrasados} onCheckedChange={setSoloAtrasados} />
              Solo atrasados
            </label>
          </div>
          {tienePermiso("pedidos.crear") && (
            <Button data-testid="pedido-nuevo-btn" onClick={() => navigate("/pedidos-especiales/nuevo")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus size={15} className="mr-1.5" /> Nuevo pedido especial
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="pedidos-tabla" className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <th className="py-2.5 pr-3 font-semibold">Número</th>
                <th className="py-2.5 pr-3 font-semibold">Cliente</th>
                <th className="py-2.5 pr-3 font-semibold">Vehículo</th>
                <th className="py-2.5 pr-3 font-semibold">Estado</th>
                <th className="py-2.5 pr-3 font-semibold">Arribo estimado</th>
                <th className="py-2.5 pr-3 text-right font-semibold">Total</th>
                <th className="py-2.5 pr-3 text-right font-semibold">Pagado</th>
                <th className="py-2.5 pr-3 text-right font-semibold">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => (
                <tr key={p.id} data-testid={`pedido-row-${p.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/pedidos-especiales/${p.id}`)}>
                  <td className="py-2.5 pr-3 font-mono text-xs font-semibold text-indigo-700">{p.numero_fmt}</td>
                  <td className="py-2.5 pr-3">{p.cliente?.nombre || "—"}</td>
                  <td className="py-2.5 pr-3">{p.vehiculo?.placa || "—"}{p.vehiculo?.modelo ? ` · ${p.vehiculo.modelo}` : ""}</td>
                  <td className="py-2.5 pr-3">
                    <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-full ${
                      p.estado === "cancelado" ? "bg-slate-100 text-slate-500" : "bg-indigo-50 text-indigo-700"}`}>
                      {ESTADO_PEDIDO_LABEL[p.estado]}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3">
                    {p.fecha_estimada_arribo ? (
                      p.estado === "recibido" || p.estado === "entregado" || p.estado === "cancelado" ? (
                        <span className="text-slate-500">{fmtFechaCorta(p.fecha_estimada_arribo)}</span>
                      ) : (
                        <span className={p.atrasado ? "font-semibold text-pink-600" : "text-slate-600"}>
                          {fmtFechaCorta(p.fecha_estimada_arribo)} · {diasLabel(p)}
                        </span>
                      )
                    ) : "—"}
                  </td>
                  <td className="py-2.5 pr-3 text-right">{colones(p.total)}</td>
                  <td className="py-2.5 pr-3 text-right text-teal-700">{colones(p.pagado)}</td>
                  <td className="py-2.5 pr-3 text-right font-semibold">{colones(p.saldo)}</td>
                </tr>
              ))}
              {pedidos.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400" data-testid="pedidos-vacio">
                  No hay pedidos especiales
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Paginacion page={page} totalPages={meta.total_pages} total={meta.total} pageSize={pageSize}
          onPage={setPage} onPageSize={setPageSize} cargando={cargando} />
      </Panel>
    </div>
  );
}
